import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import style from "./style.module.scss";
import Modal from "../../../components/modal";
import Button from "../../../components/new-button";
import TextInput from "../../../components/form/text-input";
import { toast } from "react-hot-toast";
import useUserStore from "../../../stores/user.js";
import {
  getApiTokens,
  createApiToken,
  deleteApiToken,
} from "../../../../api";

// Validation schema
const validationSchema = Yup.object({
  name: Yup.string()
    .required("Token name is required")
    .min(3, "Token name must be at least 3 characters")
    .max(50, "Token name must not exceed 50 characters"),
});

function ApiTokens({ isOpen, onClose }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [showToken, setShowToken] = useState(null);
  // Token creation is admin-only (UserController::createApiToken). Others
  // still see and can revoke any tokens they already hold.
  const canCreate = useUserStore((state) => state.user?.role === "admin");

  const initialValues = {
    name: "",
  };

  useEffect(() => {
    if (isOpen) {
      fetchTokens();
    } else {
      // Reset state when modal closes
      setNewToken(null);
      setShowToken(null);
    }
  }, [isOpen]);

  const fetchTokens = async () => {
    setLoading(true);
    try {
      const res = await getApiTokens();
      if (res.success) {
        setTokens(res.data || []);
      } else {
        toast.error(res.message || "Failed to fetch API tokens");
      }
    } catch (error) {
      toast.error("Error fetching API tokens");
      console.error("Error fetching tokens:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setCreating(true);
    const toastId = toast.loading("Creating API token...");
    try {
      const res = await createApiToken({ name: values.name });
      setSubmitting(false);
      setCreating(false);

      if (res.success) {
        const tokenData = res.data;
        setNewToken(tokenData.token);
        setShowToken(tokenData.id);
        resetForm();
        await fetchTokens();
        toast.success("API token created successfully", { id: toastId });
      } else {
        toast.error(res.message || "Failed to create API token", {
          id: toastId,
        });
      }
    } catch (error) {
      setSubmitting(false);
      setCreating(false);
      toast.error("Error creating API token", { id: toastId });
      console.error("Error creating token:", error);
    }
  };

  const handleDelete = async (tokenId) => {
    if (!window.confirm("Are you sure you want to delete this token?")) {
      return;
    }

    const toastId = toast.loading("Deleting token...");
    try {
      const res = await deleteApiToken(tokenId);
      if (res.success) {
        toast.success("Token deleted successfully", { id: toastId });
        await fetchTokens();
        if (showToken === tokenId) {
          setShowToken(null);
          setNewToken(null);
        }
      } else {
        toast.error(res.message || "Failed to delete token", { id: toastId });
      }
    } catch (error) {
      toast.error("Error deleting token", { id: toastId });
      console.error("Error deleting token:", error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Token copied to clipboard");
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header="API Tokens"
      size="md"
    >
      <div className={style.container}>
        <div className={style.docs_link} style={{ marginBottom: 16 }}>
          <a
            href="https://stratusapp.readme.io/reference/storeapplication"
            target="_blank"
            rel="noopener noreferrer"
            className={style.docs_anchor}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 14,
              color: "#2563eb",
              textDecoration: "underline",
              fontWeight: 500,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              fill="none"
              viewBox="0 0 18 18"
              style={{ marginRight: 4 }}
            >
              <path
                d="M14.25 10.5V14.25a1.5 1.5 0 0 1-1.5 1.5H4.5a1.5 1.5 0 0 1-1.5-1.5V5.25a1.5 1.5 0 0 1 1.5-1.5h3.75"
                stroke="#2563eb"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11.25 3.75h3v3"
                stroke="#2563eb"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.5 10.5 14.25 3.75"
                stroke="#2563eb"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            API Documentation
          </a>
        </div>
        {!canCreate && (
          <p>Only organization admins can create API tokens.</p>
        )}

        {/* Create New Token Form */}
        {canCreate && !newToken && (
          <div className={style.create_section}>
            <h3 className={style.section_title}>Create New Token</h3>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({
                errors,
                touched,
                isSubmitting,
                handleChange,
                values,
              }) => (
                <Form>
                  <TextInput
                    label="Token Name"
                    id="name"
                    name="name"
                    placeholder="Enter token name (e.g., Production API)"
                    error={
                      touched.name && errors.name ? errors.name : null
                    }
                    touched={touched.name}
                    errored={errors.name}
                    onChange={handleChange}
                    value={values.name}
                  />
                  <div className={style.footer}>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={onClose}
                      disabled={isSubmitting || creating}
                    >
                      <span>Cancel</span>
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting || creating}
                      isLoading={isSubmitting || creating}
                    >
                      <span>
                        {isSubmitting || creating
                          ? "Creating..."
                          : "Create Token"}
                      </span>
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}

        {/* Display New Token (only shown once after creation) */}
        {newToken && showToken && (
          <div className={style.new_token_section}>
            <div className={style.alert}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z"
                  stroke="#22A276"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 6.66667V10M10 13.3333H10.0083"
                  stroke="#22A276"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div>
                <strong>Token created successfully!</strong>
                <p>
                  Please copy this token now. You won't be able to see it again.
                </p>
              </div>
            </div>
            <div className={style.token_display}>
              <div className={style.token_input_wrapper}>
                <TextInput
                  label="Your API Token"
                  id="token"
                  name="token"
                  value={newToken}
                  readOnly
                />
                <button
                  type="button"
                  className={style.copy_button}
                  onClick={() => copyToClipboard(newToken)}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z"
                      stroke="#3A383F"
                      strokeWidth="1.33333"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3.33333 10H2.66667C2.31305 10 1.97391 9.85952 1.72386 9.60947C1.47381 9.35942 1.33333 9.02028 1.33333 8.66667V2.66667C1.33333 2.31305 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31305 1.33333 2.66667 1.33333H8.66667C9.02028 1.33333 9.35942 1.47381 9.60947 1.72386C9.85952 1.97391 10 2.31305 10 2.66667V3.33333"
                      stroke="#3A383F"
                      strokeWidth="1.33333"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Copy
                </button>
              </div>
            </div>
            <div className={style.footer}>
              <Button
                type="button"
                variant="primary"
                onClick={() => {
                  setNewToken(null);
                  setShowToken(null);
                }}
              >
                <span>Create Another Token</span>
              </Button>
            </div>
          </div>
        )}

        {/* Existing Tokens List */}
        <div className={style.tokens_section}>
          <h3 className={style.section_title}>Your API Tokens</h3>

          {loading ? (
            <div className={style.loading}>Loading tokens...</div>
          ) : tokens.length === 0 ? (
            <div className={style.empty_state}>
              <p>No API tokens created yet.</p>
            </div>
          ) : (
            <div className={style.tokens_list}>
              {tokens.map((token) => (
                <div key={token.id} className={style.token_item}>
                  <div className={style.token_info}>
                    <div className={style.token_name}>{token.name}</div>
                    <div className={style.token_meta}>
                      <span>Created: {formatDate(token.created_at)}</span>
                      {token.last_used_at && (
                        <span>
                          Last used: {formatDate(token.last_used_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    className={style.delete_button}
                    onClick={() => handleDelete(token.id)}
                    title="Delete token"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 4L4 12M4 4L12 12"
                        stroke="#DC2626"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default ApiTokens;

